// The sidebar menu (044 D1): six groups, each item's path and permission as they were.
// 047 D10 changes only the icons (Remix, D8). An item carries its icon component (`Icon`),
// so the sidebar sizes it and the breadcrumb reuses it.
import {
  RiBookOpenLine,
  RiCustomerService2Line,
  RiDashboardLine,
  RiFileList3Line,
  RiGraduationCapLine,
  RiGroupLine,
  RiHandCoinLine,
  RiHistoryLine,
  RiHomeLine,
  RiLineChartLine,
  RiNotification3Line,
  RiPriceTag3Line,
  RiSettings3Line,
  RiShieldCheckLine,
  RiShieldUserLine,
  RiTimerLine,
  RiTodoLine,
  RiUserAddLine,
  RiUserLine,
  RiUserSharedLine,
  RiVidiconLine,
  RiWallet3Line,
} from "@remixicon/react";
import { hasPermission, permissions } from "../../config/permissions";

// One icon per NAV_SECTIONS key, for the breadcrumb's group crumb.
export const SECTION_ICONS = {
  home: RiHomeLine,
  support: RiCustomerService2Line,
  people: RiGroupLine,
  money: RiWallet3Line,
  growth: RiLineChartLine,
  admin: RiShieldUserLine,
};

export const getMenuItems = (user, inboxBadge) => {
  const items = [
    {
      // 044 D2: the work queue is the first thing staff open. VIEW_DASHBOARD
      // is every staff role, so Home always has at least this row.
      text: "Queue",
      section: "home",
      Icon: RiTodoLine,
      path: "/",
      permission: permissions.VIEW_DASHBOARD,
    },
    {
      // 044 D12: the KPI page is "Reports" — the name staff use for it —
      // and it sits behind VIEW_REPORTS, the grant that names it, so a
      // moderator without that grant no longer gets a link to a page of
      // numbers. Its path stays /dashboard; `/` is the queue (slice 2).
      text: "Reports",
      section: "home",
      Icon: RiDashboardLine,
      path: "/dashboard",
      permission: permissions.VIEW_REPORTS,
    },
    {
      text: "Students",
      section: "people",
      Icon: RiUserLine,
      path: "/students",
      permission: permissions.VIEW_STUDENTS,
    },
    {
      text: "Tutors",
      section: "people",
      Icon: RiGraduationCapLine,
      path: "/tutors",
      permission: permissions.VIEW_TUTORS,
    },
    {
      // Approve/reject teacher onboarding (spec 026). Spec 044 puts the
      // link behind REVIEW_VERIFICATION — the same grant that gates the
      // approve/reject buttons on the page (Verification.js, canReview) —
      // instead of Tutors' view permission, so one grant answers for both.
      // Moderators hold it, so the queue is theirs now.
      text: "Verification",
      section: "people",
      Icon: RiShieldCheckLine,
      path: "/verification",
      permission: permissions.REVIEW_VERIFICATION,
    },
    {
      // The support console (spec 026 S3) — was "Tickets"; /tickets now
      // redirects here (App.js) so old links/bookmarks still land.
      text: "Inbox",
      section: "support",
      Icon: RiCustomerService2Line,
      path: "/inbox",
      permission: permissions.VIEW_INBOX,
      badge: inboxBadge > 0 ? String(inboxBadge) : undefined,
    },
    {
      // Free-trial requests from the website's ads landing page. Its own
      // dedicated permission (phase-2 Slice L, spec 026 D7) — staff-wide,
      // same four roles as the rules' isStaff(), including accountant, which
      // VIEW_INBOX does not carry.
      text: "Leads",
      section: "growth",
      Icon: RiUserAddLine,
      path: "/leads",
      permission: permissions.VIEW_LEADS,
    },
    {
      // 044 D14: read-only referral standing; page is a later slice.
      text: "Referrals",
      section: "growth",
      Icon: RiUserSharedLine,
      path: "/referrals",
      permission: permissions.VIEW_STUDENTS,
    },
    {
      // 044 D10: manual announcements behind the "notifications" grant (D5);
      // page is a later slice.
      text: "Notifications",
      section: "growth",
      Icon: RiNotification3Line,
      path: "/notifications",
      permission: permissions.SEND_NOTIFICATIONS,
    },
    {
      // 055 D20: the automatic reminders to students who stopped booking.
      text: "Booking reminders",
      section: "growth",
      Icon: RiTimerLine,
      path: "/booking-reminders",
      permission: permissions.SEND_NOTIFICATIONS,
    },
    {
      // 026 D7 dropped the "Schedule Lesson" entry, which pointed at /schedule.
      // Both it and /lessons rendered <Lessons />, and the removal reasoned that
      // /lessons still reached the page — true of the route, but nothing in this
      // list pointed there, so Lesson Management lost its only door. The page was
      // never the scheduling feature D7 meant to drop; it is the lesson list.
      text: "Lessons",
      section: "support",
      Icon: RiVidiconLine,
      path: "/lessons",
      permission: permissions.VIEW_LESSONS,
    },
    {
      // 044 D101: Money's first page is the Overview (Figma "Financial · Overview"); the path
      // stays /financial.
      text: "Overview",
      section: "money",
      Icon: RiWallet3Line,
      path: "/financial",
      permission: permissions.VIEW_FINANCIAL,
    },
    {
      // 044 D12: Donations is a tab of the Transactions ledger (D50–D59); this
      // entry opens the ledger page itself. D101: before Payouts, Figma's order.
      text: "Transactions",
      section: "money",
      Icon: RiFileList3Line,
      path: "/transactions",
      permission: permissions.VIEW_FINANCIAL,
    },
    {
      // 044 D16: seeing the payout run is a view, not an edit, so the link
      // asks for VIEW_FINANCIAL and moderators get the page. Mark as paid on
      // it stays behind EDIT_FINANCIAL (Payouts.js, canPay).
      text: "Payouts",
      section: "money",
      Icon: RiHandCoinLine,
      path: "/payouts",
      permission: permissions.VIEW_FINANCIAL,
    },
    {
      // 044 D11/D12: staff accounts get their own page (the Staff card that
      // lived in Settings); Add moderator / grants / disable come with the
      // page's own slice.
      text: "Staff",
      section: "admin",
      Icon: RiShieldUserLine,
      path: "/staff",
      permission: permissions.MANAGE_STAFF,
    },
    {
      text: "Subjects",
      section: "admin",
      Icon: RiBookOpenLine,
      path: "/subjects",
      permission: permissions.VIEW_SUBJECTS,
    },
    {
      text: "Plans",
      section: "admin",
      Icon: RiPriceTag3Line,
      path: "/plans",
      permission: permissions.VIEW_PLANS,
    },
    {
      text: "Settings",
      section: "admin",
      Icon: RiSettings3Line,
      path: "/settings",
      permission: permissions.VIEW_SETTINGS,
    },
    {
      text: "Activity Log",
      section: "admin",
      Icon: RiHistoryLine,
      path: "/activity-log",
      permission: permissions.VIEW_ACTIVITY_LOG,
    },
  ];

  // The merged user, as before: a grant (044 D5) opens its page for a moderator.
  return items.filter((item) => !item.permission || hasPermission(user, item.permission));
};
